/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

/**
 * PDP gallery thumbnail hover preview (desktop only).
 *
 * Behaviour:
 *   - Hovering a thumbnail in the left column immediately switches the main
 *     image to that thumbnail's media (no click required).
 *   - The aria-selected attribute moves with the previewed thumbnail (handled
 *     by slideshow.select), so the "active" outline follows the hover.
 *   - Leaving the thumbnails container without having clicked restores the
 *     previously committed slide (the one that was active before the hover
 *     started, or the most recently clicked one).
 *   - Clicking a thumbnail commits the selection — that index becomes the
 *     new restore target.
 *
 * Mobile is excluded via a min-width media query check at hover time.
 */

const DESKTOP_QUERY = '(min-width: 750px)';
const INIT_FLAG = 'data-thumb-hover-init';

/** @param {HTMLElement} gallery */
function initGallery(gallery) {
  const slideshow = gallery.querySelector('slideshow-component');
  const container = gallery.querySelector('.slideshow-controls__thumbnails-container');
  if (!slideshow || !container) return;

  const thumbnails = /** @type {HTMLElement[]} */ (
    Array.from(container.querySelectorAll(`.slideshow-controls__thumbnail:not([${INIT_FLAG}])`))
  );
  if (thumbnails.length < 2) return;

  /** Index of the slide that should be restored on mouse-leave. */
  let committedIndex = /** @type {number | null} */ (null);
  let previewing = false;

  const captureCommittedIndex = () => {
    const allThumbs = /** @type {HTMLElement[]} */ (
      Array.from(container.querySelectorAll('.slideshow-controls__thumbnail'))
    );
    const idx = allThumbs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    committedIndex = idx >= 0 ? idx : 0;
  };

  /**
   * Calls slideshow.select but keeps the thumbnails container's own scroll
   * position fixed. The slideshow internally calls `scrollIntoView` on the
   * newly active thumbnail (so it stays visible during user-initiated
   * navigation); we don't want that during hover preview, otherwise hovering
   * a thumbnail near the bottom would scroll the strip and shift every other
   * thumbnail away from the cursor.
   *
   * Because `animate: false` makes the internal centering call use
   * behavior:'instant' (synchronous), we can restore scroll right after.
   *
   * @param {number} index
   */
  const selectKeepingScroll = (index) => {
    const { scrollTop, scrollLeft } = container;
    // @ts-ignore — slideshow component exposes select()
    slideshow.select(index, undefined, { animate: false });
    container.scrollTop = scrollTop;
    container.scrollLeft = scrollLeft;
  };

  thumbnails.forEach((thumb, index) => {
    thumb.setAttribute(INIT_FLAG, 'true');

    thumb.addEventListener('mouseenter', () => {
      if (!window.matchMedia(DESKTOP_QUERY).matches) return;
      if (!previewing) {
        // Snapshot the real committed slide BEFORE we start previewing,
        // because slideshow.select() updates aria-selected on the previewed
        // thumb — we'd lose the original target otherwise.
        captureCommittedIndex();
        previewing = true;
      }
      selectKeepingScroll(index);
    });

    thumb.addEventListener('click', () => {
      // Click commits this thumb as the new restore target.
      previewing = false;
      committedIndex = index;
    });
  });

  // Attach mouseleave once per container.
  if (!container.hasAttribute(INIT_FLAG)) {
    container.setAttribute(INIT_FLAG, 'true');
    container.addEventListener('mouseleave', () => {
      if (!previewing) return;
      previewing = false;
      if (committedIndex !== null) selectKeepingScroll(committedIndex);
    });
  }
}

function initAll() {
  document.querySelectorAll('media-gallery').forEach((g) => initGallery(/** @type {HTMLElement} */ (g)));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

// Re-init after a color-variant swap. product-variations.js replaces the
// section's innerHTML and dispatches shopify:section:load — without this
// listener, thumbnails lose their hover handlers after switching color.
// (Same-product variant changes don't rebuild the gallery, so no
// variantUpdate listener is needed.)
document.addEventListener('shopify:section:load', initAll);
